import {
  isTrashable,
  requestMoveToTrashcan,
} from '@blocksuite/affine-block-streamlain';
import { isNoteBlock } from '@blocksuite/affine-block-surface';
import { toast } from '@blocksuite/affine-components/toast';
import type { Connectable } from '@blocksuite/affine-model';
import type { GfxModel } from '@blocksuite/std/gfx';

import type { EdgelessRootBlockComponent } from '../index.js';
import { isConnectable } from './query.js';

/**
 * Use deleteElementsV2 instead.
 * @deprecated
 */
export function deleteElements(
  edgeless: EdgelessRootBlockComponent,
  elements: GfxModel[]
) {
  const set = new Set(elements);
  const { service } = edgeless;

  elements.forEach(element => {
    const flavour = (element as { flavour?: string }).flavour;
    if (flavour && isTrashable(flavour)) return;
    if (isConnectable(element)) {
      const connectors = service.getConnectors(element as Connectable);
      connectors.forEach(connector => set.add(connector));
    }
  });

  // Track if any ghosts were skipped
  let ghostSkipped = false;

  set.forEach(element => {
    const flavour = (element as { flavour?: string }).flavour;

    // StreamLain E012-T013: Ghost nodes cannot be deleted by user
    // They are removed automatically when the relation is deleted
    if (flavour === 'affine:ghost') {
      ghostSkipped = true;
      return; // Skip deletion
    }

    // StreamLain: trashable blocks are only ever soft-deleted. A rejected
    // trash move must never fall through to a hard delete.
    if (flavour && isTrashable(flavour)) {
      const connectors = isConnectable(element)
        ? [...service.getConnectors(element as Connectable)]
        : [];
      requestMoveToTrashcan(edgeless.store, element.id)
        .then(outcome => {
          if (outcome === 'moved') {
            connectors.forEach(connector =>
              service.removeElement(connector.id)
            );
          }
          if (outcome === 'rejected') {
            toast(edgeless.host, 'Failed to move block to trash');
          }
        })
        .catch(error => {
          console.error('[Edgeless] Failed to move block to trash:', error);
          toast(edgeless.host, 'Failed to move block to trash');
        });
      return;
    }

    if (isNoteBlock(element)) {
      const children = edgeless.store.root?.children ?? [];
      // FIXME: should always keep at least 1 note
      if (children.length > 1) {
        edgeless.store.deleteBlock(element);
      }
    } else {
      service.removeElement(element.id);
    }
  });

  // Show toast if ghosts were skipped
  if (ghostSkipped) {
    toast(
      edgeless.host,
      'Ghost nodes cannot be deleted directly. Restore or permanently delete the original item from the trash.'
    );
  }
}
